import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { z } from "zod";

/** What kind of value a criterion takes, which decides how a list URL spells it. */
export enum WorkRecordCriterionKind {
  /** Free text, matched as part of a word. */
  Text = "Text",
  /** The id of a record or a template. */
  Id = "Id",
  /** The user id of a person, which the page can name. */
  Person = "Person",
  /** The legal entity id of a firm, which the page can name. */
  Firm = "Firm",
  /** True or false. */
  YesNo = "YesNo",
  /** An instant, written as an ISO 8601 UTC timestamp. */
  Instant = "Instant",
  /** A number. */
  Amount = "Amount",
  /** One of a fixed set of names. */
  Choice = "Choice",
}

export type WorkRecordCriterionValue = string | number | boolean | Guid | AHDate;

/** What a yes-or-no criterion is called each way, since "Hourly rate: no" reads worse than either. */
export type WorkRecordCriterionYesNo = Readonly<{ yes: string; no: string }>;

const NIL_GUID = "00000000-0000-0000-0000-000000000000";
const GuidValueZ = z.guid();
const InstantValueZ = z.union([z.iso.datetime({ offset: true }), z.iso.date()]);

/**
 * One criterion a proposal or work agreement list reads from its URL.
 *
 * The URL is the page's state: the assistant's `listUrl`, a bookmark and the page's own controls all
 * write it, and the list asks the API for exactly what it says. A criterion names the property the
 * query class holds it in and the parameter the URL carries it under — the camel-cased name of the
 * `WorkCriteria` property the API binds — and reads, writes and describes its value.
 *
 * A value that does not parse is not read: a malformed id or date in a hand-edited URL leaves the
 * criterion out rather than failing the page.
 */
export default class WorkRecordCriterion {
  public readonly property: string;
  public readonly parameter: string;
  public readonly kind: WorkRecordCriterionKind;
  /** What the chip calls the criterion. */
  public readonly label: string;
  /** For a choice, each value and what to call it. */
  public readonly choices?: Readonly<Record<string, string>>;
  /** For a yes-or-no criterion, what to call it each way. */
  public readonly yesNo?: WorkRecordCriterionYesNo;

  public constructor(params: {
    property: string;
    kind: WorkRecordCriterionKind;
    label: string;
    parameter?: string;
    choices?: Readonly<Record<string, string>>;
    yesNo?: WorkRecordCriterionYesNo;
  }) {
    this.property = params.property;
    this.parameter = params.parameter ?? params.property;
    this.kind = params.kind;
    this.label = params.label;
    this.choices = params.choices;
    this.yesNo = params.yesNo;
  }

  /** The criterion's value in `searchParams`, or undefined when it is absent or does not parse. */
  public read(searchParams: URLSearchParams): WorkRecordCriterionValue | undefined {
    const raw = searchParams.get(this.parameter);
    if (raw === null || raw === "") return undefined;
    switch (this.kind) {
      case WorkRecordCriterionKind.Text:
        return raw;
      case WorkRecordCriterionKind.Id:
      case WorkRecordCriterionKind.Person:
      case WorkRecordCriterionKind.Firm:
        return GuidValueZ.safeParse(raw).success && raw !== NIL_GUID ? new Guid(raw) : undefined;
      case WorkRecordCriterionKind.YesNo: {
        const lower = raw.toLowerCase();
        if (lower === "true") return true;
        if (lower === "false") return false;
        return undefined;
      }
      case WorkRecordCriterionKind.Instant: {
        if (!InstantValueZ.safeParse(raw).success) return undefined;
        const instant = new AHDate(raw);
        return instant.isValid ? instant : undefined;
      }
      case WorkRecordCriterionKind.Amount: {
        const amount = Number(raw);
        return raw.trim() !== "" && Number.isFinite(amount) ? amount : undefined;
      }
      case WorkRecordCriterionKind.Choice:
        return Object.keys(this.choices ?? {}).find((choice) => choice.toLowerCase() === raw.toLowerCase());
    }
  }

  /** The value as a list URL spells it, and as the API binds it. */
  public write(value: WorkRecordCriterionValue): string {
    if (value instanceof Guid) return value.value;
    if (value instanceof AHDate) return value.toISOString();
    if (typeof value === "boolean") return value ? "true" : "false";
    return String(value);
  }

  /**
   * What the chip for this criterion says.
   *
   * @param value The criterion's value.
   * @param name For a person or a firm, its name, when the page knows it. Without it the chip says
   *   only what the criterion is, never an id.
   */
  public describe(value: WorkRecordCriterionValue, name?: string): string {
    switch (this.kind) {
      case WorkRecordCriterionKind.YesNo:
        return value === true ? (this.yesNo?.yes ?? this.label) : (this.yesNo?.no ?? `Not ${this.label}`);
      case WorkRecordCriterionKind.Choice: {
        const choice = String(value);
        return `${this.label}: ${(this.choices?.[choice] ?? choice).trim()}`;
      }
      case WorkRecordCriterionKind.Instant:
        // The API's day bounds are UTC days, so the day is read in UTC too: midnight UTC on the 1st
        // is still the 1st, wherever the reader is.
        return value instanceof AHDate ? `${this.label} ${value.asMoment.clone().utc().format("MM/DD/YYYY")}` : this.label;
      case WorkRecordCriterionKind.Person:
      case WorkRecordCriterionKind.Firm:
        return name ? `${this.label}: ${name}` : this.label;
      case WorkRecordCriterionKind.Id:
        return this.label;
      case WorkRecordCriterionKind.Amount:
        return `${this.label} ${String(value)}`;
      case WorkRecordCriterionKind.Text:
        return `${this.label}: ${String(value)}`;
    }
  }
}
